import type { CSSProperties } from 'react';
import { isFirmRole } from '@domain';
import type { Translate } from '../i18n/index.js';
import { theme } from '../theme.js';
import type { Action, Role } from '../state.js';
import type { Lang } from '../i18n/index.js';
import type { ConnectionState, TenantConnection } from '@domain';

interface HeaderProps {
  crumbRoot: string;
  crumbLeaf: string;
  role: Role;
  /** Only CFOAI staff are offered the preview at all. */
  canPreview: boolean;
  previewing: boolean;
  /** The open company's Xero connection, or null while it is unknown. */
  connection: TenantConnection | null;
  lang: Lang;
  t: Translate;
  dispatch: (action: Action) => void;
}

/**
 * How the badge should look for each state.
 *
 * REAUTH and FAILED are red and say so. The whole point of the connection
 * work was that a withdrawn connection must stop claiming to be healthy, and
 * a badge that is green whatever has happened undoes it in the one place
 * everybody looks.
 */
function connectionTone(state: ConnectionState): {
  bg: string;
  border: string;
  dot: string;
  text: string;
  label: string;
} {
  switch (state) {
    case 'OK':
      return {
        bg: theme.goodBg,
        border: theme.goodBorder,
        dot: theme.good,
        text: theme.goodDeep,
        label: 'Connected',
      };
    case 'EXPIRING':
      return {
        bg: '#F8F3E7',
        border: '#E9DEC4',
        dot: '#B4801E',
        text: '#7E590F',
        label: 'Expiring soon',
      };
    default:
      return {
        bg: '#F8EDEC',
        border: '#EBD2D1',
        dot: '#A94442',
        text: '#8A3230',
        label: 'Reconnect needed',
      };
  }
}

function segment(active: boolean, extra: CSSProperties = {}): CSSProperties {
  return {
    flex: '0 0 auto',
    padding: '0 10px',
    height: '24px',
    lineHeight: 1,
    border: 0,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '11px',
    background: active ? theme.ink : '#fff',
    color: active ? '#fff' : theme.textMuted,
    ...extra,
  };
}

export function Header({
  crumbRoot,
  crumbLeaf,
  role,
  canPreview,
  previewing,
  connection,
  lang,
  t,
  dispatch,
}: HeaderProps) {
  // The client-view toggle is a CFOAI affordance: it previews what somebody
  // else would see, which only makes sense if you are not already them.
  const isAdmin = isFirmRole(role);
  const isArabic = lang === 'ar';

  return (
    <header
      style={{
        flex: '0 0 auto',
        minHeight: '64px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '10px 12px',
        padding: '12px 18px',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flex: '0 0 auto',
          minWidth: 'max-content',
          fontSize: '12.5px',
          color: theme.textMuted,
          whiteSpace: 'nowrap',
        }}
      >
        <span>{crumbRoot}</span>
        <span style={{ color: '#D4D4D8' }}>/</span>
        <span style={{ color: theme.text, fontWeight: 600 }}>{crumbLeaf}</span>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: '#fff',
          border: 0,
          boxShadow: theme.shadowPill,
          borderRadius: theme.radiusPill,
          padding: '8px 12px',
          flex: '1 1 160px',
          minWidth: 0,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={theme.textSubtle} strokeWidth="2">
          <circle cx="11" cy="11" r="7" />
          <path d="M20 20l-4-4" />
        </svg>
        <input
          placeholder={`${t('Search')}…`}
          aria-label={t('Search')}
          style={{ flex: '1 1 40px', minWidth: 0, border: 0, background: 'transparent', fontSize: '12.5px' }}
        />
      </div>

      <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: '12px', whiteSpace: 'nowrap' }}>
        {/*
          This badge was a green dot, the word Connected and "27m", all three
          written into the file. It said the same thing for every company, in
          every state, for ever — including for a company whose Xero
          connection had been withdrawn, which is the one occasion anybody
          needed it to say something else.

          It now reads the connection this company actually has. Absent while
          that is still loading, because an unknown state is not a healthy one
          and a green dot is a promise.
        */}
        {isAdmin && connection && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
              background: connectionTone(connection.state).bg,
              border: `1px solid ${connectionTone(connection.state).border}`,
              borderRadius: theme.radiusPill,
              padding: '6px 11px',
            }}
            title={connection.lastSyncSub}
          >
            <span
              style={{
                width: '7px',
                height: '7px',
                borderRadius: '50%',
                background: connectionTone(connection.state).dot,
              }}
            />
            <span
              style={{
                fontSize: '12px',
                fontWeight: 600,
                color: connectionTone(connection.state).text,
              }}
            >
              Xero · {t(connectionTone(connection.state).label)}
            </span>
            <span
              style={{
                fontFamily: theme.fontMono,
                fontSize: '11px',
                color: connectionTone(connection.state).text,
                opacity: 0.8,
              }}
            >
              {connection.lastSync}
            </span>
          </div>
        )}

{/*
          Was a control that claimed to change who you were. It changed nothing
          the server believed, which made it both useless and misleading. It is
          now what it can honestly be: a way for someone at CFOAI to look at a
          client's view. It grants nothing and takes nothing away.
        */}
        {canPreview && (
          <div style={{ display: 'flex', alignItems: 'stretch', height: '26px', border: `1px solid #EDE7E2`, borderRadius: theme.radiusPill, overflow: 'hidden' }}>
            <button
              onClick={() => previewing && dispatch({ type: 'togglePreviewAsClient' })}
              style={segment(!previewing)}
              title={t('Your own view')}
            >
              {t('Admin')}
            </button>
            <button
              onClick={() => !previewing && dispatch({ type: 'togglePreviewAsClient' })}
              style={segment(previewing)}
              title={t('See what a client sees. Changes nothing.')}
            >
              {t('Client view')}
            </button>
          </div>
        )}

        <div style={{ fontFamily: theme.fontMono, fontSize: '12px', color: theme.textMuted }}>QAR</div>

        <div style={{ display: 'flex', alignItems: 'stretch', height: '26px', border: `1px solid #EDE7E2`, borderRadius: theme.radiusPill, overflow: 'hidden' }}>
          <button
            onClick={() => dispatch({ type: 'setLang', lang: 'en' })}
            style={segment(!isArabic, { width: '34px', padding: 0, letterSpacing: '.06em' })}
          >
            EN
          </button>
          <button
            onClick={() => dispatch({ type: 'setLang', lang: 'ar' })}
            style={segment(isArabic, { width: '34px', padding: 0, letterSpacing: '.06em', fontFamily: theme.fontArabic })}
          >
            AR
          </button>
        </div>
      </div>
    </header>
  );
}
